import find from 'lodash/find'
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'
import { PERMISSION_TYPES } from '@signal/core/permissions/constants'

export const DASHBOARD_FEATURE_KEY = 'meridianDashboard'
export const INDUSTRY_REPORTS_FEATURE_KEY = 'industryReports'
export const PERFORMANCE_FEATURE_KEY = 'performance'
export const ACTIVATE_FEATURE_KEY = 'activate'
export const LOCAL_SEARCH_FEATURE_KEY = DASHBOARD_FEATURE_KEY
export const VALUE_INTELLIGENCE_FEATURE_KEY = DASHBOARD_FEATURE_KEY
export const DEALER_PRO_FEATURE_KEY = 'dealerPro'
export const MARKET_VIEW_FEATURE_KEY = 'marketView'
export const PROSPECT_MARKET_VIEW_FEATURE_KEY = 'prospectMarketView'
export const RYLIE_AI_CHATBOT_FEATURE_KEY = 'RylieAIChatBot'

export const LEGACY_SIGNAL_PERMISSION_NAME_MAP = {
  [PERMISSION_TYPES.SIGNAL_ACCESS]: PERFORMANCE_FEATURE_KEY,
  [PERMISSION_TYPES.SIGNAL_ACTIVATION]: ACTIVATE_FEATURE_KEY,
  [PERMISSION_TYPES.MERIDIAN_DASHBOARD]: DASHBOARD_FEATURE_KEY,
  [PERMISSION_TYPES.INDUSTRY_REPORT]: INDUSTRY_REPORTS_FEATURE_KEY,
  [PERMISSION_TYPES.DEALER_PRO]: DEALER_PRO_FEATURE_KEY,
  [PERMISSION_TYPES.MARKET_VIEW]: MARKET_VIEW_FEATURE_KEY,
  [PERMISSION_TYPES.PROSPECT_MARKET_VIEW]: PROSPECT_MARKET_VIEW_FEATURE_KEY
}

export const NEW_SIGNAL_PERMISSION_NAME_MAP = {
  [PERFORMANCE_FEATURE_KEY]: PERMISSION_TYPES.SIGNAL_ACCESS,
  [ACTIVATE_FEATURE_KEY]: PERMISSION_TYPES.SIGNAL_ACTIVATION,
  [DASHBOARD_FEATURE_KEY]: PERMISSION_TYPES.MERIDIAN_DASHBOARD,
  [INDUSTRY_REPORTS_FEATURE_KEY]: PERMISSION_TYPES.INDUSTRY_REPORT,
  [DEALER_PRO_FEATURE_KEY]: PERMISSION_TYPES.DEALER_PRO,
  [MARKET_VIEW_FEATURE_KEY]: PERMISSION_TYPES.MARKET_VIEW,
  [PROSPECT_MARKET_VIEW_FEATURE_KEY]: PERMISSION_TYPES.PROSPECT_MARKET_VIEW,
  [RYLIE_AI_CHATBOT_FEATURE_KEY]: PERMISSION_TYPES.RYLIE_AI_CHATBOT
}

export const PERMISSION_LEVEL_READ = 'read'
export const PERMISSION_LEVEL_WRITE = 'write'
export const PERMISSION_LEVEL_ADMIN = 'admin'
export const PERMISSION_LEVEL_DENY = 'deny'

const PERMISSION_LEVEL_MASKS = {
  [PERMISSION_LEVEL_DENY]: 0x0,
  [PERMISSION_LEVEL_READ]: 0x1,
  // write includes read
  [PERMISSION_LEVEL_WRITE]: 0x3,
  // admin includes read & write
  [PERMISSION_LEVEL_ADMIN]: 0x7
}

export const hasPermission = (
  accessibleFeatures,
  {
    feature: neededFeature = '',
    permissionName: neededPermission = '',
    level: neededLevel
  }
) => {
  const neededLevelMask = PERMISSION_LEVEL_MASKS[neededLevel]
  if (isUndefined(neededLevelMask)) {
    throw new Error(
      `hasPermission requested invalid level type '${neededLevel}' for '${neededFeature}'`
    )
  }
  const accessibleFeature = find(
    accessibleFeatures,
    featureCur =>
      featureCur.feature === neededFeature ||
      featureCur.permissionName === neededPermission
  )
  const accessibleLevel = get(accessibleFeature, 'level')
  const accessibleLevelMask = PERMISSION_LEVEL_MASKS[accessibleLevel] || 0
  if (isUndefined(accessibleLevelMask)) {
    throw new Error(
      `hasPermission saw invalid level type '${accessibleLevel}' for '${neededFeature}' in accessibleFeatures`
    )
  }
  return (accessibleLevelMask & neededLevelMask) === neededLevelMask
}

/**
 * This function checks the input features to see if it contains all the required permissions passed
 * in as the second argument
 *
 * @param {Array} accessibleFeatures the account features, taken from the active account object
 * @param {Array} neededPermissions an array of permission objects in the form of {feature, permission}
 * for example, { feature: 'performance', permission: 'write' }. When possible, use the constants
 * defined at the top of this file
 */
export const hasAllPermissions = (accessibleFeatures, neededPermissions) =>
  !neededPermissions ||
  neededPermissions.reduce(
    (hasAllPermissions, permissionCur) =>
      hasAllPermissions && hasPermission(accessibleFeatures, permissionCur),
    true
  )
